import { Box, Grid, Stack } from "@mui/material";
import type { DeliveryType } from "@types_def/models/delivery.types";
import { useParams } from "react-router-dom";
import { DeliveryDetailsCard } from "@/components/Forms/createDeliveryDeal/delivery-details-card";
import { DeliveryPricingSection } from "@/components/Forms/createDeliveryDeal/delivery-pricing-section";
import { DeliveryScheduleMapSection } from "@/components/Forms/createDeliveryDeal/delivery-schedule-map-section";
import { useDeliveryDealForm } from "@/components/Forms/createDeliveryDeal/hooks/use-delivery-deal-form";
import { MarginWarningDialog } from "@/components/Forms/marginWarningDialog";
import { useFindDeliveryByIdQuery } from "@/libs/generated/hooks";
import { buildMissionFromDelivery } from "@/utils/delivery-mission";
import { calculateMargin } from "@/utils/delivery-pricing";
import { strToEnum } from "@/utils/strEnum";

export default function CreateDeliveryDeal() {
  const { type, contract } = useParams();
  if (type == null || contract == null) throw new Error("Type and contract are required");

  const deliveryType = strToEnum<DeliveryType>(type);
  const deliveryQuery = useFindDeliveryByIdQuery({ GetByIdInput: { id: Number(contract) } });
  const { data, isPending, error } = deliveryQuery;
  const delivery = data?.findDeliveryById;

  const {
    form,
    marginWarningOpen,
    handleClientPriceChange,
    handleMarginWarningConfirm,
    handleMarginWarningCancel,
  } = useDeliveryDealForm({ delivery });

  if (error != null) {
    return (
      <p>
        Error:
        {JSON.stringify(error)}
      </p>
    );
  }

  if (!isPending && delivery == null) return <p>Delivery data is not found</p>;
  if (delivery == null) return null;

  const missions = buildMissionFromDelivery(delivery, deliveryType);

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: "1600px", mx: "auto" }}>
      <Stack spacing={3}>
        <DeliveryDetailsCard delivery={delivery} />

        <Box
          component="form"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <Stack spacing={3}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 5 }}>
                <DeliveryScheduleMapSection delivery={delivery} form={form} missions={missions} />
              </Grid>

              <Grid size={{ xs: 12, md: 7 }}>
                <DeliveryPricingSection form={form} onClientPriceChange={handleClientPriceChange} />
              </Grid>
            </Grid>
          </Stack>
        </Box>

        <form.Subscribe
          selector={(state) => ({
            clientFinalPrice: state.values.clientFinalPrice,
            wantedPrice: state.values.wantedPrice,
          })}
        >
          {({ clientFinalPrice, wantedPrice }) => (
            <MarginWarningDialog
              open={marginWarningOpen}
              margin={calculateMargin(clientFinalPrice, wantedPrice)}
              onConfirm={handleMarginWarningConfirm}
              onCancel={handleMarginWarningCancel}
            />
          )}
        </form.Subscribe>
      </Stack>
    </Box>
  );
}
